import React, { useEffect, useState, useMemo } from 'react';
import { ref, onValue, update } from 'firebase/database';
import { db } from '../firebase/config';
import { FiSearch, FiX, FiInfo, FiCalendar, FiDollarSign, FiUser, FiPackage, FiNavigation, FiCreditCard, FiEdit, FiPhone } from 'react-icons/fi';

// STATUS_CONFIG with order progression
const STATUS_CONFIG = {
  pending: { color: 'pending', icon: '⏱', label: 'Pending' },
  confirmed: { color: 'confirmed', icon: '✓', label: 'Confirmed' },
  Dispatched: { color: 'Dispatched', icon: '🚚', label: 'Dispatched' },
  delivered: { color: 'delivered', icon: '✓', label: 'Delivered' },
  cancelled: { color: 'cancelled', icon: '✕', label: 'Cancelled' },
};

// Define status progression flow
const STATUS_FLOW = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['Dispatched', 'cancelled'],
  Dispatched: ['delivered'],
  delivered: [], // Final state
  cancelled: [], // Terminal state
};

const CompanyOrdersTable = () => {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    
    if (typeof address === 'string') return address;
    
    if (typeof address === 'object') {
      const parts = [];
      if (address.street) parts.push(address.street);
      if (address.landmark) parts.push(address.landmark);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.postalCode) parts.push(address.postalCode);
      
      return parts.length > 0 ? parts.join(', ') : 'N/A';
    }
    
    return 'N/A';
  };

  const handleCancelOrder = (orderId) => {
    if (window.confirm('Are you sure you want to cancel this order?')) {
      const orderRef = ref(db, `HTAMS/orders/${orderId}`);
      update(orderRef, { status: 'cancelled' })
        .then(() => alert('Order cancelled successfully.'))
        .catch((err) => {
          console.error('Cancel error:', err);
          alert('Failed to cancel order.');
        });
    }
  };

  useEffect(() => {
    const htamsUser = JSON.parse(localStorage.getItem('htamsUser') || '{}');
    const currentUid = htamsUser?.uid;
    const ordersRef = ref(db, 'HTAMS/orders');
    
    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val() || {};
      const orderList = Object.entries(data)
        .map(([id, val]) => ({ 
          id, 
          ...val,
          date: val.orderDate || val.date,
          amount: calculateAmount(val)
        }))
        .filter(order => order.placedBy === currentUid);
      setOrders(orderList);
    });

    return () => unsubscribe();
  }, []);

  const calculateAmount = (order) => {
    if (order.totalAmount) return order.totalAmount;
    if (order.amount) return order.amount;
    if (order.price) return order.price;
    if (order.quantity && order.unitPrice) return order.quantity * order.unitPrice;
    return 0;
  };

  // UPDATED - Display all product names
  const getProductName = (order) => {
    if (order.productName) return order.productName;
    if (order.items && order.items.length > 0) {
      return order.items.map(item => item.productName || 'N/A').join(', ');
    }
    return 'N/A';
  };

  // UPDATED - Sum all quantities
  const getQuantity = (order) => {
    if (order.quantity) return order.quantity;
    if (order.totalItems) return order.totalItems;
    if (order.items && order.items.length > 0) {
      return order.items.reduce((total, item) => total + (item.quantity || 0), 0);
    }
    return 0;
  };

  const handleViewInvoice = (order) => {
    const invoiceUrl = order?.invoice?.downloadURL;
    if (!invoiceUrl) {
      alert('Invoice is not available for this order yet.');
      return;
    }
    window.open(invoiceUrl, '_blank', 'noopener,noreferrer');
  };

  // Handle Edit Button Click
  const handleEditClick = (order) => {
    setIsEditMode(true);
    setEditFormData({
      customerAddress: typeof order.customerAddress === 'string' 
        ? order.customerAddress 
        : formatAddress(order.customerAddress),
      customerMobile: order.customerMobile || order.mobile || '',
      status: order.status || 'confirmed'
    });
  };

  // Handle Status Change
  const handleStatusChange = async (orderId, newStatus) => {
    if (window.confirm(`Are you sure you want to change status to ${STATUS_CONFIG[newStatus]?.label}?`)) {
      setIsUpdating(true);
      const orderRef = ref(db, `HTAMS/orders/${orderId}`);
      
      try {
        await update(orderRef, { 
          status: newStatus,
          updatedAt: new Date().toISOString()
        });
        alert('Status updated successfully!');
        setSelectedOrder({ ...selectedOrder, status: newStatus });
        setEditFormData({ ...editFormData, status: newStatus });
      } catch (err) {
        console.error('Status update error:', err);
        alert('Failed to update status.');
      } finally {
        setIsUpdating(false);
      }
    }
  };

  // Handle Save Edit
  const handleSaveEdit = async () => {
    if (!selectedOrder) return;

    // Validate mobile number
    if (editFormData.customerMobile && !/^\d{10}$/.test(editFormData.customerMobile)) {
      alert('Please enter a valid 10-digit mobile number.');
      return;
    }

    if (!editFormData.customerAddress || editFormData.customerAddress.trim() === '') {
      alert('Address cannot be empty.');
      return;
    }

    setIsUpdating(true);
    const orderRef = ref(db, `HTAMS/orders/${selectedOrder.id}`);

    try {
      const updateData = {
        customerAddress: editFormData.customerAddress,
        customerMobile: editFormData.customerMobile,
        updatedAt: new Date().toISOString()
      };

      await update(orderRef, updateData);
      alert('Order updated successfully!');
      setIsEditMode(false);
      setSelectedOrder({
        ...selectedOrder,
        ...updateData
      });
    } catch (err) {
      console.error('Update error:', err);
      alert('Failed to update order.');
    } finally {
      setIsUpdating(false);
    }
  };

  const filteredOrders = useMemo(() => {
    let result = [...orders];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(order =>
        order.customerName?.toLowerCase().includes(term) ||
        getProductName(order)?.toLowerCase().includes(term) ||
        order.id?.toLowerCase().includes(term)
      );
    }
    
    if (activeFilter !== 'all') {
      result = result.filter(order => 
        (order.status || 'confirmed').toLowerCase() === activeFilter
      );
    }
    
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return result;
  }, [orders, searchTerm, activeFilter, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const statusStats = useMemo(() => {
    const stats = { all: orders.length };
    orders.forEach(order => {
      const status = (order.status || 'confirmed').toLowerCase();
      stats[status] = (stats[status] || 0) + 1;
    });
    return stats;
  }, [orders]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      // Format as DD/MM/YYYY
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Check if order can be edited (only address & mobile for pending orders)
  const canEditDetails = (order) => {
    return order.status?.toLowerCase() === 'pending';
  };

  // Get available status transitions
  const getAvailableStatusTransitions = (currentStatus) => {
    const status = currentStatus?.toLowerCase() || 'confirmed';
    return STATUS_FLOW[status] || [];
  };

  return (
    <>
      <style>{mobileOrdersStyles}</style>
      <div className="mobile-orders-page">
        {/* Header Section */}
        <div className="mobile-orders-header">
          <h1 className="mobile-orders-title">
            <span className="mobile-title-icon">📦</span>
            Order Management
          </h1>
          <p className="mobile-orders-subtitle">Track and manage your company's orders</p>

          {/* Stats Overview */}
          <div className="mobile-stats-overview">
            <div className="mobile-stat-card">
              <div className="mobile-stat-number">{orders.length}</div>
              <div className="mobile-stat-label">Total Orders</div>
            </div>
          
            <div className="mobile-stat-card">
              <div className="mobile-stat-number">{statusStats.Dispatched || 0}</div>
              <div className="mobile-stat-label">Dispatched</div>
            </div>
            <div className="mobile-stat-card">
              <div className="mobile-stat-number">{statusStats.delivered || 0}</div>
              <div className="mobile-stat-label">Delivered</div>
            </div>
          </div>
        </div>

        {/* Search Section */}
        <div className="mobile-search-section">
          <div className="mobile-search-container">
            <div className="mobile-search-input-wrapper">
              <FiSearch className="mobile-search-icon" />
              <input
                type="text"
                placeholder="Search orders by customer, product or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mobile-search-input"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')} 
                  className="mobile-clear-btn"
                  style={{
                    position: 'absolute',
                    right: 'var(--mobile-space-md)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--mobile-text-tertiary)',
                    cursor: 'pointer',
                    padding: 'var(--mobile-space-xs)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <FiX />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="mobile-filter-section">
          <div className="mobile-filter-controls">
            <div className="mobile-filter-group">
              <label className="mobile-filter-label">Status Filter</label>
              <select 
                className="mobile-filter-select"
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
              >
                <option value="all">All ({statusStats.all || 0})</option>
                {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                  <option key={status} value={status}>
                    {config.label} ({statusStats[status] || 0})
                  </option>
                ))}
              </select>
            </div>

            <div className="mobile-filter-group">
              <label className="mobile-filter-label">Sort By</label>
              <select 
                className="mobile-filter-select"
                value={sortConfig.key}
                onChange={(e) => {
                  setSortConfig({ key: e.target.value, direction: 'desc' });
                }}
              >
                <option value="date">Date</option>
                <option value="amount">Amount</option>
                <option value="customerName">Customer</option>
              </select>
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div className="mobile-orders-list">
          {filteredOrders.length === 0 ? (
            <div className="mobile-empty-state">
              <span className="mobile-empty-icon">📋</span>
              <p className="mobile-empty-text">No orders found</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setActiveFilter('all');
                }}
                className="mobile-reset-button"
              >
                Reset filters
              </button>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <div key={order.id} className="mobile-order-card">
                <div className="mobile-order-header">
                  <span className="mobile-order-id">#{order.id.slice(0, 10)}</span>
                  <span className={`mobile-status-badge ${STATUS_CONFIG[order.status?.toLowerCase()]?.color || 'confirmed'}`}>
                    {STATUS_CONFIG[order.status?.toLowerCase()]?.label || 'Confirmed'}
                  </span>
                </div>

                <div className="mobile-order-date">{formatDate(order.date)}</div>

                <div className="mobile-order-details">
                  <div className="mobile-detail-row">
                    <FiUser className="mobile-detail-icon" />
                    <div className="mobile-detail-content">
                      <span className="mobile-detail-label">Customer</span>
                      <span className="mobile-detail-value">{order.customerName || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="mobile-detail-row">
                    <FiPackage className="mobile-detail-icon" />
                    <div className="mobile-detail-content">
                      <span className="mobile-detail-label">Product</span>
                      <span className="mobile-detail-value">{getProductName(order)}</span>
                    </div>
                  </div>

                  <div className="mobile-detail-row">
                    <FiDollarSign className="mobile-detail-icon" />
                    <div className="mobile-detail-content">
                      <span className="mobile-detail-label">Amount</span>
                      <span className="mobile-detail-value mobile-amount">{formatCurrency(order.amount)}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setSelectedOrder(order);
                    setIsEditMode(false);
                  }}
                  className="mobile-view-button"
                >
                  <FiInfo />
                  View Details
                </button>
                {order.invoice?.downloadURL && (
                  <button
                    onClick={() => handleViewInvoice(order)}
                    className="mobile-invoice-button"
                  >
                    Invoice
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Modal */}
        {selectedOrder && (
          <div className="mobile-modal-overlay" onClick={() => {
            setSelectedOrder(null);
            setIsEditMode(false);
          }}>
            <div className="mobile-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="mobile-modal-header">
                <h2 className="mobile-modal-title">
                  {isEditMode ? 'Edit Order' : 'Order Details'}
                </h2>
                <button onClick={() => {
                  setSelectedOrder(null);
                  setIsEditMode(false);
                }} className="mobile-modal-close">
                  <FiX />
                </button>
              </div>

              <div className="mobile-modal-status">
                <span className={`mobile-status-badge large ${STATUS_CONFIG[selectedOrder.status?.toLowerCase()]?.color || 'confirmed'}`}>
                  {STATUS_CONFIG[selectedOrder.status?.toLowerCase()]?.icon} {STATUS_CONFIG[selectedOrder.status?.toLowerCase()]?.label || 'Confirmed'}
                </span>
              </div>

              <div className="mobile-modal-body">
                {/* Edit Mode */}
                {isEditMode ? (
                  <div className="mobile-edit-form">
                    <div className="mobile-form-group">
                      <label className="mobile-form-label">
                        <FiNavigation /> Delivery Address
                      </label>
                      <textarea
                        className="mobile-form-textarea"
                        value={editFormData.customerAddress}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          customerAddress: e.target.value
                        })}
                        rows="3"
                        placeholder="Enter delivery address"
                      />
                    </div>

                    <div className="mobile-form-group">
                      <label className="mobile-form-label">
                        <FiPhone /> Mobile Number
                      </label>
                      <input
                        type="tel"
                        className="mobile-form-input"
                        value={editFormData.customerMobile}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          customerMobile: e.target.value
                        })}
                        placeholder="Enter 10-digit mobile number"
                        maxLength="10"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    {/* View Mode - Order Details */}
                    <div className="mobile-modal-detail-group">
                      <div className="mobile-modal-icon-box user">
                        <FiUser />
                      </div>
                      <div className="mobile-modal-detail-info">
                        <h3>Customer</h3>
                        <p>{selectedOrder.customerName || 'N/A'}</p>
                      </div>
                    </div>

                    {/* UPDATED - Compact Product Display */}
                    {selectedOrder.items && selectedOrder.items.length > 0 ? (
                      <div className="mobile-modal-detail-group">
                        <div className="mobile-modal-icon-box product">
                          <FiPackage />
                        </div>
                        <div className="mobile-modal-detail-info">
                          <h3>Products ({selectedOrder.items.length})</h3>
                          <div className="product-items-compact">
                            {selectedOrder.items.map((item, index) => (
                              <div key={index} className="product-item-row">
                                <span className="product-name">{item.productName || 'N/A'}</span>
                                <span className="product-details">
                                  <span className="product-qty">×{item.quantity}</span>
                                  <span className="product-price">{formatCurrency(item.totalPrice)}</span>
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mobile-modal-detail-group">
                        <div className="mobile-modal-icon-box product">
                          <FiPackage />
                        </div>
                        <div className="mobile-modal-detail-info">
                          <h3>Product</h3>
                          <p>{getProductName(selectedOrder)}</p>
                        </div>
                      </div>
                    )}

                    <div className="mobile-modal-detail-group">
                      <div className="mobile-modal-icon-box amount">
                        <FiDollarSign />
                      </div>
                      <div className="mobile-modal-detail-info">
                        <h3>Total Amount</h3>
                        <p className="amount-text">{formatCurrency(selectedOrder.amount)}</p>
                      </div>
                    </div>

                    <div className="mobile-modal-detail-group">
                      <div className="mobile-modal-icon-box date">
                        <FiCalendar />
                      </div>
                      <div className="mobile-modal-detail-info">
                        <h3>Date</h3>
                        <p>{formatDate(selectedOrder.date)}</p>
                      </div>
                    </div>

                    {getQuantity(selectedOrder) > 0 && (
                      <div className="mobile-modal-detail-group">
                        <div className="mobile-modal-icon-box quantity">
                          <span>Qty</span>
                        </div>
                        <div className="mobile-modal-detail-info">
                          <h3>Total Quantity</h3>
                          <p>{getQuantity(selectedOrder)}</p>
                        </div>
                      </div>
                    )}

                    {selectedOrder.paymentMethod && (
                      <div className="mobile-modal-detail-group">
                        <div className="mobile-modal-icon-box payment">
                          <FiCreditCard />
                        </div>
                        <div className="mobile-modal-detail-info">
                          <h3>Payment Method</h3>
                          <p>{selectedOrder.paymentMethod}</p>
                        </div>
                      </div>
                    )}

                    {selectedOrder.customerMobile && (
                      <div className="mobile-modal-detail-group">
                        <div className="mobile-modal-icon-box phone">
                          <FiPhone />
                        </div>
                        <div className="mobile-modal-detail-info">
                          <h3>Mobile Number</h3>
                          <p>{selectedOrder.customerMobile}</p>
                        </div>
                      </div>
                    )}

                    {selectedOrder.customerAddress && (
                      <div className="mobile-modal-detail-group">
                        <div className="mobile-modal-icon-box address">
                          <FiNavigation />
                        </div>
                        <div className="mobile-modal-detail-info">
                          <h3>Delivery Address</h3>
                          <p>{formatAddress(selectedOrder.customerAddress)}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mobile-modal-footer">
                <div className="mobile-modal-footer-buttons">
                  {isEditMode ? (
                    <>
                      <button
                        onClick={() => setIsEditMode(false)}
                        className="mobile-modal-action-btn secondary"
                        disabled={isUpdating}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        className="mobile-modal-action-btn primary"
                        disabled={isUpdating}
                      >
                        {isUpdating ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  ) : (
                    <>
                      {canEditDetails(selectedOrder) && (
                        <button
                          onClick={() => handleEditClick(selectedOrder)}
                          className="mobile-modal-action-btn edit"
                        >
                          <FiEdit /> Edit Details
                        </button>
                      )}
                      {selectedOrder.invoice?.downloadURL && (
                        <button
                          onClick={() => handleViewInvoice(selectedOrder)}
                          className="mobile-modal-action-btn secondary"
                        >
                          View Invoice
                        </button>
                      )}
                      <button 
                        onClick={() => {
                          setSelectedOrder(null);
                          setIsEditMode(false);
                        }} 
                        className="mobile-modal-action-btn"
                      >
                        Close
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const mobileOrdersStyles = `
  :root {
    --mobile-space-xs: 4px;
    --mobile-space-sm: 8px;
    --mobile-space-md: 16px;
    --mobile-space-lg: 24px;
    --mobile-space-xl: 32px;
    --mobile-radius-sm: 8px;
    --mobile-radius-md: 12px;
    --mobile-radius-lg: 16px;
    --mobile-text-sm: 13px;
    --mobile-text-base: 15px;
    --mobile-text-lg: 18px;
    --mobile-text-xl: 24px;
    --mobile-text-2xl: 28px;
    --mobile-border-light: #e5e7eb;
    --mobile-primary: #6366f1;
    --mobile-text-primary: #1a1a1a;
    --mobile-text-tertiary: #6b7280;
  }

  .mobile-orders-page {
    background: #f5f5f7;
    min-height: 100vh;
    padding: 16px;
    padding-bottom: 100px;
    max-width: 100vw;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  * {
    box-sizing: border-box;
  }

  .mobile-orders-header {
    margin-bottom: 16px;
  }

  .mobile-orders-title {
    font-size: 24px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 6px 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .mobile-title-icon {
    font-size: var(--mobile-text-xl);
  }

  .mobile-orders-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 12px 0;
  }

  .mobile-stats-overview {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-top: 12px;
  }

  .mobile-stat-card {
    background: white;
    border-radius: 12px;
    padding: 12px 8px;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .mobile-stat-number {
    font-size: 24px;
    font-weight: 700;
    color: #6366f1;
    margin-bottom: 4px;
    line-height: 1;
  }

  .mobile-stat-label {
    font-size: 11px;
    color: #6b7280;
    font-weight: 500;
    line-height: 1.2;
    word-wrap: break-word;
    text-align: center;
  }

  .mobile-search-section {
    margin-bottom: 14px;
  }

  .mobile-search-container {
    display: flex;
    gap: var(--mobile-space-md);
    align-items: center;
  }

  .mobile-search-input-wrapper {
    flex: 1;
    position: relative;
    display: flex;
    width: 100%;
    align-items: center;
  }

  .mobile-search-icon {
    position: absolute;
    left: var(--mobile-space-md);
    color: var(--mobile-text-tertiary);
    font-size: var(--mobile-text-lg);
    z-index: 1;
  }

  .mobile-search-input {
    padding: 12px 16px;
    padding-left: 40px;
    width: 100%;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: white;
    font-size: 14px;
    color: #1a1a1a;
    outline: none;
  }

  .mobile-search-input:focus {
    border-color: var(--mobile-primary);
  }

  .mobile-search-button {
    white-space: nowrap;
  }

  .mobile-filter-section {
    margin-bottom: 14px;
  }

  .mobile-filter-controls {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .mobile-filter-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
  }

  .mobile-filter-label {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a1a;
  }

  .mobile-filter-select {
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: white;
    font-size: 13px;
    color: #1a1a1a;
    outline: none;
  }

  .mobile-filter-select:focus {
    border-color: var(--mobile-primary);
  }

  .mobile-orders-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .mobile-order-card {
    background: white;
    border-radius: 12px;
    padding: 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
  }

  .mobile-order-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .mobile-order-id {
    font-family: 'SF Mono', 'Monaco', 'Courier New', monospace;
    color: #6366f1;
    font-weight: 600;
    font-size: 13px;
  }

  .mobile-status-badge {
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .mobile-status-badge.pending { background-color: #f59e0b; }
  .mobile-status-badge.confirmed { background-color: #6366f1; }
  .mobile-status-badge.Dispatched { background-color: #8b5cf6; }
  .mobile-status-badge.delivered { background-color: #10b981; }
  .mobile-status-badge.cancelled { background-color: #ef4444; }
  .mobile-status-badge.large {
    padding: 8px 16px;
    font-size: 11px;
  }

  .mobile-order-date {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 10px;
  }

  .mobile-order-details {
    margin-bottom: 10px;
  }

  .mobile-detail-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    border-bottom: 1px solid #f3f4f6;
  }

  .mobile-detail-row:last-child {
    border-bottom: none;
  }

  .mobile-detail-icon {
    color: #6366f1;
    font-size: 16px;
    flex-shrink: 0;
  }

  .mobile-detail-content {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .mobile-detail-label {
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
  }

  .mobile-detail-value {
    font-size: 13px;
    color: #1a1a1a;
    font-weight: 600;
    text-align: right;
    max-width: 60%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-detail-value.mobile-amount {
    color: #10b981;
    font-weight: 700;
  }

  .mobile-view-button {
    width: 100%;
    padding: 11px;
    background-color: #6366f1;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.2s;
  }

  .mobile-invoice-button {
    width: 100%;
    margin-top: 8px;
    padding: 11px;
    background-color: #f3f4f6;
    color: #6366f1;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .mobile-invoice-button:active {
    transform: scale(0.98);
    background-color: #e5e7eb;
  }

  .mobile-view-button:active {
    transform: scale(0.98);
    background-color: #4f46e5;
  }

  .mobile-empty-state {
    text-align: center;
    padding: 32px 20px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .mobile-empty-icon {
    font-size: 48px;
    opacity: 0.4;
    display: block;
    margin-bottom: 12px;
  }

  .mobile-empty-text {
    font-size: 15px;
    font-weight: 600;
    color: #6b7280;
    margin: 0 0 16px 0;
  }

  .mobile-reset-button {
    padding: 11px 24px;
    background-color: #6366f1;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  .product-items-compact {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 4px;
  }

  .product-item-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 10px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }

  .product-name {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a1a;
    flex: 1;
    margin-right: 12px;
  }

  .product-details {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .product-qty {
    font-size: 12px;
    font-weight: 700;
    color: #6366f1;
    background: #e0e7ff;
    padding: 3px 8px;
    border-radius: 6px;
  }

  .product-price {
    font-size: 13px;
    font-weight: 700;
    color: #10b981;
  }

  .mobile-status-change-section {
    background: #f8f9fa;
    padding: 16px;
    border-radius: 12px;
    margin-bottom: 16px;
  }

  .mobile-section-title {
    font-size: 14px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 12px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .mobile-status-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .mobile-status-change-btn {
    width: 100%;
    padding: 12px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    color: white;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .mobile-status-change-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .mobile-status-change-btn.confirmed { background-color: #6366f1; }
  .mobile-status-change-btn.confirmed:active:not(:disabled) { background-color: #4f46e5; }
  .mobile-status-change-btn.Dispatched { background-color: #8b5cf6; }
  .mobile-status-change-btn.Dispatched:active:not(:disabled) { background-color: #7c3aed; }
  .mobile-status-change-btn.delivered { background-color: #10b981; }
  .mobile-status-change-btn.delivered:active:not(:disabled) { background-color: #059669; }
  .mobile-status-change-btn.cancelled { background-color: #ef4444; }
  .mobile-status-change-btn.cancelled:active:not(:disabled) { background-color: #dc2626; }

  .mobile-edit-form {
    padding: 16px 0;
  }

  .mobile-form-group {
    margin-bottom: 20px;
  }

  .mobile-form-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .mobile-form-input,
  .mobile-form-textarea {
    width: 100%;
    padding: 12px;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    color: #1a1a1a;
    background: white;
    outline: none;
    transition: border-color 0.2s;
  }

  .mobile-form-input:focus,
  .mobile-form-textarea:focus {
    border-color: #6366f1;
  }

  .mobile-form-textarea {
    resize: vertical;
    min-height: 80px;
    font-family: inherit;
  }

  .mobile-modal-overlay {
    position: fixed;
    inset: 0;
    bottom: 60px;
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.3s ease-out;
  }

  .mobile-modal-content {
    background-color: white;
    width: 100%;
    max-height: 85vh;
    height: 85vh;
    display: flex;
    flex-direction: column;
    border-radius: 16px 16px 0 0;
    animation: slideUp 0.3s ease-out;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }

  .mobile-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid var(--mobile-border-light);
    background: white;
    position: sticky;
    top: 0;
    z-index: 10;
    flex-shrink: 0;
  }

  .mobile-modal-title {
    font-size: var(--mobile-text-xl);
    font-weight: 700;
    color: var(--mobile-text-primary);
    margin: 0;
  }

  .mobile-modal-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    background-color: #E63946;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 16px;
    flex-shrink: 0;
    transition: all 0.2s;
  }

  .mobile-modal-close:active {
    transform: scale(0.95);
    background-color: #d62839;
  }

  .mobile-modal-status {
    padding: 12px 16px;
    text-align: center;
    border-bottom: 1px solid #e5e7eb;
    background: linear-gradient(to bottom, #f8f9fa 0%, white 100%);
    flex-shrink: 0;
  }

  .mobile-modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px 16px;
    padding-bottom: 16px;
    background: #fafbfc;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }

  .mobile-modal-detail-group {
    display: flex;
    gap: var(--mobile-space-md);
    margin-bottom: var(--mobile-space-lg);
  }

  .mobile-modal-icon-box {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 18px;
  }

  .mobile-modal-icon-box.user { background-color: #dbeafe; color: #2563eb; }
  .mobile-modal-icon-box.product { background-color: #e9d5ff; color: #9333ea; }
  .mobile-modal-icon-box.amount { background-color: #d1fae5; color: #10b981; }
  .mobile-modal-icon-box.date { background-color: #fef3c7; color: #f59e0b; }
  .mobile-modal-icon-box.quantity {
    background-color: #e0e7ff;
    color: #6366f1;
    font-weight: 700;
    font-size: var(--mobile-text-sm);
  }
  .mobile-modal-icon-box.payment { background-color: #cffafe; color: #06b6d4; }
  .mobile-modal-icon-box.phone { background-color: #ddd6fe; color: #7c3aed; }
  .mobile-modal-icon-box.address { background-color: #fce7f3; color: #ec4899; }

  .mobile-modal-detail-info {
    flex: 1;
    min-width: 0;
  }

  .mobile-modal-detail-info h3 {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 4px 0;
  }

  .mobile-modal-detail-info p {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
    word-wrap: break-word;
    line-height: 1.4;
  }

  .mobile-modal-detail-info p.amount-text {
    font-size: var(--mobile-text-lg);
    font-weight: 700;
    color: #10b981;
  }

  .mobile-modal-footer {
    padding: 12px 16px;
    padding-bottom: 16px;
    border-top: 1px solid var(--mobile-border-light);
    background: white;
    position: sticky;
    bottom: 0;
    flex-shrink: 0;
    z-index: 10;
  }

  .mobile-modal-footer-buttons {
    display: flex;
    flex-direction: column;
    gap: var(--mobile-space-sm);
  }

  .mobile-modal-action-btn {
    width: 100%;
    padding: var(--mobile-space-md);
    background-color: #6366f1;
    color: white;
    border: none;
    border-radius: var(--mobile-radius-md);
    font-size: var(--mobile-text-base);
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.2s;
  }

  .mobile-modal-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .mobile-modal-action-btn.secondary {
    background-color: #f3f4f6;
    color: #6366f1;
    border: 1px solid var(--mobile-border-light);
  }

  .mobile-modal-action-btn.secondary:active:not(:disabled) {
    background-color: #e5e7eb;
  }

  .mobile-modal-action-btn.primary {
    background-color: #10b981;
  }

  .mobile-modal-action-btn.primary:active:not(:disabled) {
    background-color: #059669;
  }

  .mobile-modal-action-btn.edit {
    background-color: #f59e0b;
  }

  .mobile-modal-action-btn.edit:active:not(:disabled) {
    background-color: #d97706;
  }

  .mobile-modal-action-btn:active:not(:disabled) {
    background-color: #4f46e5;
    transform: scale(0.98);
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }

  /* RESPONSIVE BREAKPOINTS - 380px to 500px FIX */
  @media (min-width: 380px) and (max-width: 500px) {
    .mobile-orders-page {
      padding: 14px;
      padding-bottom: 100px;
    }

    .mobile-orders-title {
      font-size: 22px;
    }

    .mobile-orders-subtitle {
      font-size: 13px;
    }

    .mobile-stats-overview {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }

    .mobile-stat-card {
      padding: 16px 12px;
      min-height: 85px;
    }

    .mobile-stat-number {
      font-size: 26px;
      margin-bottom: 6px;
    }

    .mobile-stat-label {
      font-size: 12px;
      line-height: 1.3;
    }

    .mobile-search-input {
      padding: 11px 16px 11px 40px;
      font-size: 14px;
    }

    .mobile-filter-select {
      padding: 10px 12px;
      font-size: 13px;
    }

    .mobile-order-card {
      padding: 14px;
    }

    .mobile-detail-value {
      font-size: 13px;
      max-width: 60%;
    }

    .mobile-view-button,
    .mobile-invoice-button {
      padding: 11px 16px;
      font-size: 14px;
    }
  }

  @media (max-width: 379px) {
    .mobile-orders-page {
      padding: 12px;
      padding-bottom: 100px;
    }

    .mobile-orders-title {
      font-size: 20px;
    }

    .mobile-stats-overview {
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }

    .mobile-stat-card {
      padding: 12px 8px;
      min-height: 75px;
    }

    .mobile-stat-number {
      font-size: 22px;
    }

    .mobile-stat-label {
      font-size: 10px;
    }

    .mobile-order-card {
      padding: 12px;
    }
  }

  @media (min-width: 430px) and (max-width: 500px) {
    .mobile-orders-page {
      padding: 16px;
    }

    .mobile-stats-overview {
      gap: 14px;
    }

    .mobile-stat-card {
      padding: 18px 14px;
      min-height: 90px;
    }

    .mobile-stat-number {
      font-size: 28px;
    }

    .mobile-stat-label {
      font-size: 12px;
    }
  }

  @media (min-width: 380px) and (max-width: 500px) and (orientation: landscape) {
    .mobile-orders-page {
      padding: 12px;
      padding-bottom: 80px;
    }

    .mobile-stats-overview {
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
    }

    .mobile-stat-card {
      padding: 12px 8px;
      min-height: 70px;
    }

    .mobile-stat-number {
      font-size: 22px;
    }

    .mobile-stat-label {
      font-size: 10px;
    }
  }

  @media (max-width: 480px) {
    .mobile-stats-overview {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }
  }

  @media (max-width: 768px) {
    .mobile-orders-page {
      padding: var(--mobile-space-md);
    }
    
    .mobile-search-container {
      flex-direction: column;
      gap: var(--mobile-space-sm);
    }
    
    .mobile-search-button {
      width: 100%;
    }
    
    .mobile-filter-controls {
      flex-direction: column;
      gap: var(--mobile-space-sm);
    }
    
    .mobile-filter-group {
      min-width: auto;
    }
  }

  @media (min-width: 769px) {
    .mobile-orders-page {
      padding: var(--mobile-space-lg);
    }

    .mobile-modal-overlay {
      align-items: center;
      padding: var(--mobile-space-lg);
    }

    .mobile-modal-content {
      max-width: 600px;
      border-radius: var(--mobile-radius-lg);
    }
  }
`;


export default CompanyOrdersTable;
